<script>
import WorkerBalanceFilter from '@/views/WorkerBalance/Components/WorkerBalanceFilter.vue';
import useSharedData from "@/use/SharedData.js";
import useTools from "@/use/Tools.js";
import {useWorkerBalanceStore} from "@/stores/WorkerBalance.js";
import {computed, ref, watch} from "vue";
import WorkerBalanceInsOuts from "@/views/WorkerBalance/Components/WorkerBalanceInsOuts.vue";
import {useSecurityStore} from "@/stores/Security";
import {storeToRefs} from "pinia";
import axios from "axios";
import useSharedMethod from "@/use/SharedMethod";

export default {
    props: {
        vacation: Boolean,
        rootPath: String,
    },
    components: {
        WorkerBalanceFilter,
        WorkerBalanceInsOuts,
    },
    setup(props) {
        const {loading, total} = useSharedData();
        const {transformFilterValues, priceFormat} = useSharedMethod();
        const {$i18n, $router} = useTools();
        
        const securityStore = useSecurityStore();
        const workerBalanceStore = useWorkerBalanceStore();
        const {workerBalance} = storeToRefs(workerBalanceStore);
        
        const rootPath = computed(() => props.vacation ? 'vacation_balance' : 'absence_requests_balance');
        
        const filter = ref({
            phase: "ACTIVE",
            field: "remain",
            operation: "greaterThanOrEqualTo",
        });
        
        const headers = computed(() => {
            let result = [];
            
            result.push(
                {
                    text: "...",
                    align: "start",
                    sortable: false,
                    value: "action",
                    width: "0"
                },
                {
                    text: $i18n.t("common.code"),
                    align: "start",
                    sortable: true,
                    value: "workerCode",
                    width: "0"
                },
                {
                    text: $i18n.t("module.worker"),
                    align: "start",
                    sortable: true,
                    value: "workerLastName",
                    width: "0"
                },
                {
                    text: $i18n.t("common.name"),
                    align: "start",
                    sortable: true,
                    value: "balanceName",
                });
            
            if (!props.vacation && filter.value.start) {
                result.push({
                        text: $i18n.t("common.value_period"),
                        align: "start",
                        sortable: false,
                        value: "valuePeriod",
                    },
                    {
                        text: $i18n.t("common.taken_period"),
                        align: "start",
                        sortable: false,
                        value: "takenPeriod",
                    },
                    {
                        text: $i18n.t("common.remain_period"),
                        align: "start",
                        sortable: false,
                        value: "restPeriod",
                    });
            }
            
            if (props.vacation) {
                result.push({
                    text: $i18n.t("balance.totalRemaining"),
                    align: "start",
                    sortable: true,
                    value: "totalRemaining",
                });
            }
            
            result.push(
                {
                    text: $i18n.t("balance.value"),
                    align: "start",
                    sortable: true,
                    value: "value",
                },
                {
                    text: $i18n.t("balance.total"),
                    align: "start",
                    sortable: true,
                    value: "total",
                },
                {
                    text: $i18n.t("balance.taken"),
                    align: "start",
                    sortable: true,
                    value: "totalTaken",
                },
                {
                    text: $i18n.t("balance.remain"),
                    align: "start",
                    sortable: true,
                    value: "totalRest",
                },);
            
            if (props.vacation) {
                result.push(
                    {
                        text: $i18n.t("balance.plate_base"),
                        align: "start",
                        sortable: true,
                        value: "plateBase"
                    },
                )
            }
            
            return result;
        });
        
        const actions = [
            {
                title: $i18n.t('balance.manual_produce'),
                icon: 'mdi-plus-circle-outline',
                color: 'teal',
                access: (item) => item.balanceAllowManualProduce,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `${rootPath.value}.manual.produce`, params: {id: item.id, byDate: !item.balanceActivateConsolidation}})
                },
            },
            {
                title: $i18n.t('balance.manual_consume'),
                icon: 'mdi-minus-circle-outline',
                color: 'red',
                access: (item) => item.balanceAllowManualConsume,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `${rootPath.value}.manual.consume`, params: {id: item.id, byDate: !item.balanceActivateConsolidation}})
                },
            },
            {
                title: $i18n.t('balance.type_MANUAL_GATHERING'),
                icon: 'mdi-sigma',
                color: 'blue darken-2',
                access: () => props.vacation,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'vacation_balance.manual.gathering', params: {id: item.id}})
                },
            },
        ];
        
        const searchParams = ref({
            sortBy: "workerCode",
            descending: false,
            page: 1,
            itemsPerPage: 5
        });
        
        function handleSearch(params) {
            loading.value = true;
            workerBalanceStore.setWorkerBalances([]);
            total.value = 0;
            
            if (props.vacation) {
                filter.value.balanceId = securityStore.userContext.vacationBalanceId;
            } else {
                filter.value.notEqualBalanceId = securityStore.userContext.vacationBalanceId;
            }
            
            loading.value = true;
            filter.value.page = params.page - 1;
            filter.value.rowsPerPage = params.itemsPerPage;
            filter.value.sortBy = params.sortBy;
            filter.value.descending = params.descending;
            
            axios.get('/workerBalances', {params: transformFilterValues(filter.value)}).then((response) => {
                workerBalanceStore.setWorkerBalances(response.data.elements);
                total.value = response.data.total;
            }).finally(() => {
                loading.value = false;
            });
        }
        
        function findAll() {
            handleSearch(searchParams.value)
        }
        
        watch(() => filter.value, () => {
            searchParams.page = 1;
            findAll();
        }, {deep: true, immediate: true});
        
        watch(searchParams, (newParams) => {
            handleSearch(newParams);
        }, {deep: true});
        
        function clearFilter() {
            Object.keys(filter.value).forEach(key => {
                if (key === 'phase') {
                    filter.value[key] = 'ACTIVE';
                } else if (key === 'annexId') {
                    // filter.value[key] = annexStore.getMax();
                } else {
                    filter.value[key] = null;
                }
            });
        }
        
        function itemClick(item) {
            workerBalance.value = item;
        }
        
        const remainingDetailsDialog = ref(false);
        const selectedItem = ref({remainings: []});
        
        function openRemainingDetails(item) {
            selectedItem.value = item;
            remainingDetailsDialog.value = true;
        }
        
        function closeRemainingDetails() {
            remainingDetailsDialog.value = false;
            selectedItem.value = {remainings: []};
        }
        
        
        return {
            securityStore,
            workerBalanceStore,
            loading,
            total,
            filter,
            headers,
            rootPath,
            remainingDetailsDialog,
            selectedItem,
            searchParams,
            actions,
            openRemainingDetails,
            closeRemainingDetails,
            findAll,
            priceFormat,
            clearFilter,
            itemClick,
        }
    },
};
</script>

<template>
    <div>
        <w-app
            :title="vacation ? $t('module.balance_vacation') : $t('module.balance')"
            show-total
            :total="total"
            show-filter
            :filter="filter"
            @clearFilter="clearFilter"
        >
            <template v-slot:filter>
                <worker-balance-filter v-model="filter" :vacation="vacation"/>
            </template>
            
            <template v-slot:rightToolbar>
                <w-btn-reload :loading="loading" @press="findAll"/>
                <!--<w-btn
                    v-if="securityStore.authorities['UPDATE_WORKER_BALANCE']"
                    color="teal"
                    icon="mdi-cash-register"
                    :text="'Initialiser'"
                    @press="$router.push({ name: `${path}.init` })"
                />-->
            </template>
            
            <template v-slot:content>
                <w-server-data-table
                    v-model="searchParams"
                    :actions="actions"
                    :headers="headers"
                    :items="workerBalanceStore.workerBalances"
                    :loading="loading"
                    select-first
                    @itemClick="itemClick"
                    :total="total"
                    :root-path="rootPath"
                    module="WORKER_BALANCE"
                >
                    <template v-slot:[`item.workerCode`]="{ item }">
                        <w-chip :text="item.workerCode"/>
                    </template>
                    <template v-slot:[`item.workerLastName`]="{ item }">
                        <w-inline-text bold :text="item.workerName"/>
                    </template>
                    <template v-slot:[`item.balanceName`]="{ item }">
                        <w-inline-text :text="item.balanceName"/>
                    </template>
                    <template v-slot:[`item.valuePeriod`]="{ item }">
                        <w-chip outlined :text="item.valuePeriod"/>
                    </template>
                    <template v-slot:[`item.takenPeriod`]="{ item }">
                        <w-chip outlined :text="item.takenPeriod"/>
                    </template>
                    <template v-slot:[`item.restPeriod`]="{ item }">
                        <w-chip outlined :text="item.valuePeriod - item.takenPeriod"/>
                    </template>
                    <template v-slot:[`item.total`]="{ item }">
                        <w-chip outlined :text="item.total"/>
                    </template>
                    <template v-slot:[`item.totalTaken`]="{ item }">
                        <w-chip outlined :text="item.totalTaken"/>
                    </template>
                    <template v-slot:[`item.value`]="{ item }">
                        <w-chip outlined :text="item.value"/>
                    </template>
                    <template v-slot:[`item.totalRest`]="{ item }">
                        <w-chip outlined :text="item.totalRest"/>
                    </template>
                    <template v-slot:[`item.totalRemaining`]="{ item }">
                        <w-chip
                            v-if="item.balanceActivateConsolidation && item.balanceConsolidationType === 'REMAINING'"
                            outlined
                            icon="mdi-chevron-right"
                            :text="item.totalRemaining"
                            @press="openRemainingDetails(item)"
                        />
                        <v-icon v-else color="red">mdi-cancel</v-icon>
                    </template>
                    <template v-slot:[`item.plateBase`]="{ item }">
                        <w-chip color="teal" :text="priceFormat(item.plateBase)"/>
                    </template>
                </w-server-data-table>
                <WorkerBalanceInsOuts
                    :path="rootPath"
                    :start="filter.start"
                    :end="filter.end"
                    :start-month="filter.startMonth"
                    :end-month="filter.endMonth"
                    :vacation="vacation"
                />
            
            </template>
        </w-app>
        <v-dialog
            v-model="remainingDetailsDialog"
            persistent
            max-width="600"
            scrollable
        >
            <v-card>
                <w-toolbar color="teal" :title="selectedItem.workerName"/>
                <v-card-text>
                    <v-container fluid>
                        <v-simple-table dense>
                            <template v-slot:default>
                                <thead>
                                <tr>
                                    <th class="text-left">Nom</th>
                                    <th class="text-left">Valeur</th>
                                    <th class="text-left" v-if="vacation">Salaire Congé</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr
                                    v-for="remaining in selectedItem.remainings"
                                    :key="remaining.id"
                                >
                                    <td>{{ remaining.name }}</td>
                                    <td>{{ remaining.value }}</td>
                                    <td v-if="vacation">{{ priceFormat(remaining.plateBase) }}</td>
                                </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="closeRemainingDetails"/>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <router-view/>
    </div>
</template>

<style lang="scss" scoped>
div.tabs [role="tab"] {
    justify-content: flex-start;
}
</style>
